import { useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import useCart from "@/hooks/useCartV2";
import { existProductCart } from "@/utils/cartV2";
import { calculatePercentageDiscount } from "@/utils/money";

interface IUseProductCardGallery {
  item?: Cart;
}

export default function useProductCardGallery({ item }: IUseProductCardGallery) {
  const navigate = useNavigate();
  const { items, handleAddCart, handleRemove, handleDecrement, handleChangeInput, ...props } = useCart();
  const [showInput, setShowInput] = useState(false);
  const [userIsTyping, setUserIsTyping] = useState(false);

  const cartItem = item ? existProductCart(items, +item?.product?.sku) : null;

  const productQuantity = useMemo(() => {
    return cartItem?.quantity ?? 0;
  }, [cartItem?.quantity]);

  const productFinalPricePerStepWeight = useMemo(() => {
    return (item?.price.finalPrice ?? 0) * (item?.details?.averageWeight || 1);
  }, [item?.price.finalPrice, item?.details?.averageWeight]);

  const productOriginalPricePerStepWeight = useMemo(() => {
    return (item?.price.originalPrice ?? 0) * (item?.details?.averageWeight || 1);
  }, [item?.price.originalPrice, item?.details?.averageWeight]);

  const productFinalPrice = useMemo(() => {
    if (!showInput) {
      return productFinalPricePerStepWeight;
    }

    return (item?.price.finalPrice ?? 0) * (productQuantity || 1);
  }, [item?.price.finalPrice, productQuantity, productFinalPricePerStepWeight, showInput]);

  const productOriginalPrice = useMemo(() => {
    if (!showInput) {
      return productOriginalPricePerStepWeight;
    }

    return (item?.price.originalPrice ?? 0) * (productQuantity || 1);
  }, [item?.price.originalPrice, productQuantity, item?.details.averageWeight, showInput]);

  const gramOrUnitShort = useMemo(() => {
    if ((item?.details.averageWeight || 1) >= 2 && item?.details.measureTypeDisplayShort) {
      return `${item.details.averageWeight || 1}${item?.details.measureTypeDisplayShort}`;
    }
    return "uni";
  }, [item?.details?.averageWeight, item?.details.measureTypeDisplayShort]);

  const getGramans = useCallback(
    (productQty: number) => {
      if ((cartItem?.details?.averageWeight || 1) >= 2 && cartItem?.details.measureTypeDisplayShort) {
        return cartItem?.details.measureTypeDisplayShort;
      }
      return productQty === 1 ? "unidad" : "unidades";
    },
    [cartItem?.details?.averageWeight, cartItem?.details.measureTypeDisplayShort]
  );

  const isProductAvailable = useMemo(() => {
    return item?.details?.available;
  }, [item?.details.available, item?.details?.averageWeight, item?.details.maxQtyToOrder]);

  const percentageDiscount = useMemo(
    () =>
      calculatePercentageDiscount({
        priceDiscount: item?.price.finalPrice ?? 0,
        priceOriginal: item?.price.originalPrice ?? 0,
      }),
    [item?.price.originalPrice, item?.price.finalPrice]
  );

  const handleAddToCart = () => {
    if (!isProductAvailable || !item) {
      return;
    }
    setUserIsTyping(false);
    handleAddCart(item);
  };

  const handledToCleanCart = () => {
    if (!item) {
      return;
    }
    handleRemove(+item.product.sku, () => {
      setShowInput(false);
      setUserIsTyping(false);
    });
  };

  const handleToDecrement = () => {
    if (!item) {
      return;
    }
    handleDecrement(+item.product.sku, () => {
      setShowInput(false);
      setUserIsTyping(false);
    });
  };

  const handleToChangeInput = (quantity: number) => {
    if (!item) {
      return;
    }
    setUserIsTyping(true);
    if (!quantity) {
      handleChangeInput(+item.product.sku, "");
      setShowInput(true);
      return;
    }
    handleChangeInput(+item.product.sku, quantity.toString());
  };

  const handleGoToBrand = (e: MouseEvent<HTMLHeadingElement>) => {
    e.preventDefault();

    // Go to brand page
    navigate(`/b/${item?.product.brand.slug}`);
  };

  useEffect(() => {
    if (!userIsTyping) {
      setShowInput(productQuantity >= 1);
    }
  }, [productQuantity, userIsTyping]);

  useEffect(() => {
    const $counterElement = document.getElementById("counter-input");

    const handleMouseDown = (e: MouseEvent) => {
      if ($counterElement && !$counterElement.contains(e.target as Node)) {
        if (productQuantity === 0) {
          handledToCleanCart();
        }
      }
    };

    if (showInput) {
      document.addEventListener("mousedown", handleMouseDown);
      return () => {
        document.removeEventListener("mousedown", handleMouseDown);
      };
    }
  }, [showInput, productQuantity, item?.details?.averageWeight]);

  return {
    // states:
    showInput,
    productQuantity,
    isProductAvailable,
    percentageDiscount,
    productFinalPricePerStepWeight,
    productOriginalPricePerStepWeight,
    productFinalPrice,
    productOriginalPrice,
    gramOrUnitShort,

    // functions:
    getGramans,
    handleAddToCart,
    handledToCleanCart,
    handleToDecrement,
    handleToChangeInput,
    handleGoToBrand,

    // rest:
    ...props,
  };
}
